import { type ApolloClient } from "@apollo/client";
import {
  Templates,
  type Post,
  type ProfileLayoutModel,
  type ProfileRestingModelEntry,
} from "@package/ui-gql/index-data-access";
import { uniqueId } from "lodash";
import {
  type LayoutPostPosition,
  type SliceWallScreenState,
} from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import {
  getProfileFromProfileSelectFlyout,
  getRestingModelFromFullScreenFlyout,
} from "../utils/service-utils";
import { ProfileLayoutModelManager } from "../utils/wall-manager-profile";
import { ProfileLayoutModelEntryManager } from "../utils/wall-manager-profile-layout";
import {
  getLayout,
  getLocationActiveProfile,
  getPost,
  getPosts,
  getProfile,
} from "../utils/wall-utils-gql";

const DEFAULT_STALE_FLYOUT_TIMEOUT = 60;

type LayoutEngineEvents = {
  "screen-updated": (state: SliceWallScreenState) => void;
  "request-resting": (resting: ProfileRestingModelEntry) => void;
  "profile-changed": (profile: string) => void;
};

class LayoutEngine extends EventEmitter<LayoutEngineEvents> {
  public readonly id: string = uniqueId();

  private activating: boolean = false;
  private managers: ProfileLayoutModelEntryManager[] = [];

  private timeoutTileAssetChange?: ReturnType<typeof setTimeout>;
  private timeoutTileFlyoutStale?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly managerLayout: ProfileLayoutModelManager,
    private readonly layout: ProfileLayoutModel,
    private readonly screenIndex: number,
  ) {
    super();
  }

  public destroy(): void {
    super.destroy();
    this.cancelTimeoutLayoutRotation();
    this.cancelTimeoutFlyoutStale();
    console.log(
      `[wall-engine-layout] Destroyed index ${this.screenIndex} (${this.id})`,
    );
  }

  public async activate(): Promise<void> {
    if (this.activating) {
      return;
    }
    this.activating = true;
    console.log(
      `[wall-engine-layout] Activating index ${this.screenIndex} (${this.id})`,
    );

    this.managers = [];
    this.cancelTimeoutLayoutRotation();

    if (this.layout.layout) {
      const model = await getLayout(this.client, this.layout.layout);
      if (model) {
        const count =
          (model.template === Templates.Custom
            ? model.custom?.positions?.length
            : model.tileCount) ?? 0;

        if (this.layout.detailed && this.layout.items) {
          for (let i = 0; i < (this.layout.items?.length ?? 0); i++) {
            this.managers.push(
              new ProfileLayoutModelEntryManager(
                this.client,
                this.layout.layout,
                this.layout.items[i] ?? {},
                this.managerLayout,
              ),
            );
          }
        } else {
          for (let i = 0; i < count; i++) {
            this.managers.push(
              new ProfileLayoutModelEntryManager(
                this.client,
                this.layout.layout,
                this.layout.item ?? {},
                this.managerLayout,
              ),
            );
          }
        }

        await Promise.all(this.managers.map((m) => m.initialize()));
        await this.updateAllLayoutPositions();
      }
    }

    this.activating = false;
    console.log(
      `[wall-engine-layout] Activated index ${this.screenIndex} (${this.id})`,
    );
  }

  public async handlePostInteracted(
    position: LayoutPostPosition,
  ): Promise<void> {
    console.log(
      `[wall-engine-layout] Post interacted ${position.post.id} - index ${this.screenIndex} (${this.id})`,
    );

    this.cancelTimeoutFlyoutStale();

    if (position.post?.flyoutType) {
      if (position.post.flyoutType === "61111c78003c346c54a26814") {
        const resting = getRestingModelFromFullScreenFlyout(position.post);
        if (resting) {
          this.emit("request-resting", resting);
          console.log(
            `[wall-engine-layout] Requesting resting for post ${position.post.id} - index ${this.screenIndex} (${this.id})`,
          );
        }
      } else if (position.post?.flyoutType === "60365c78003c344a72a26814") {
        const profile = getProfileFromProfileSelectFlyout(position.post);
        if (profile) {
          this.emit("profile-changed", profile);
          console.log(
            `[wall-engine-layout] Requesting profile change for post ${position.post.id} - index ${this.screenIndex} (${this.id})`,
          );
        }
      } else {
        this.cancelTimeoutLayoutRotation();

        if (this.layout.layout) {
          const model = await getLayout(this.client, this.layout.layout);
          if (model) {
            this.startTimeoutFlyoutStale(position.post);
            this.emit("screen-updated", {
              id: this.id,
              active: true,
              flyout: {
                position: this.layout.flyout?.position ?? undefined,
                post: position.post,
                relatedPosts: position.relatedPosts,
                staleTimeout:
                  position.post.staleFlyoutTimeout ||
                  model.content?.staleFlyoutTimeout ||
                  DEFAULT_STALE_FLYOUT_TIMEOUT,
              },
              index: this.screenIndex,
              layout: model,
              layoutPostPositions: this.managers
                .map((manager) => manager.getActiveAsset())
                .filter((entry) => !!entry),
              menu: false,
              position: this.layout.position ?? undefined,
            });
            console.log(
              `[wall-engine-layout] Activated flyout for post ${position.post.id} - index ${this.screenIndex} (${this.id})`,
            );
          }
        }
      }
    }
  }

  public async handlePostFlyoutDirectActivation(postId: string): Promise<void> {
    console.log(
      `[wall-engine-layout] Directly activating flyout for post ${postId} - index ${this.screenIndex} (${this.id})`,
    );

    this.cancelTimeoutFlyoutStale();

    const post = await getPost(this.client, postId);
    if (post) {
      this.cancelTimeoutLayoutRotation();

      if (this.layout.layout) {
        const model = await getLayout(this.client, this.layout.layout);
        if (model) {
          this.startTimeoutFlyoutStale(post);

          this.emit("screen-updated", {
            id: this.id,
            active: true,
            flyout: {
              position: this.layout.flyout?.position ?? undefined,
              post,
              relatedPosts: post.relatedPosts
                ? await getPosts(this.client, post.relatedPosts)
                : [],
              staleTimeout:
                post.staleFlyoutTimeout ||
                model.content?.staleFlyoutTimeout ||
                DEFAULT_STALE_FLYOUT_TIMEOUT,
            },
            index: this.screenIndex,
            layout: model,
            layoutPostPositions: this.managers
              .map((manager) => manager.getActiveAsset())
              .filter((entry) => !!entry),
            menu: false,
            position: this.layout.position ?? undefined,
          });
          console.log(
            `[wall-engine-layout] Activated flyout for post ${postId} - index ${this.screenIndex} (${this.id})`,
          );
        }
      }
    }
  }

  public async handlePostChange(): Promise<void> {
    console.log(
      `[wall-engine-layout] Handling post change - index ${this.screenIndex} (${this.id})`,
    );

    await this.updateAllLayoutPositions();
  }

  public async handleScreenMenuOpened(): Promise<void> {
    this.cancelTimeoutLayoutRotation();

    if (this.layout.layout) {
      const model = await getLayout(this.client, this.layout.layout);
      if (model) {
        this.emit("screen-updated", {
          id: this.id,
          active: true,
          index: this.screenIndex,
          layout: model,
          layoutPostPositions: this.managers
            .map((manager) => manager.getActiveAsset())
            .filter((entry) => !!entry),
          menu: true,
          position: this.layout.position ?? undefined,
        });
        console.log(
          `[wall-engine-layout] Screen menu opened - index ${this.screenIndex} (${this.id})`,
        );
      }
    }
  }

  public async handleScreenMenuClosed(): Promise<void> {
    console.log(
      `[wall-engine-layout] Screen menu closed - index ${this.screenIndex} (${this.id})`,
    );

    await this.updateAllLayoutPositions();
  }

  public handleFlyoutClosed(): void {
    console.log(
      `[wall-engine-layout] Flyout closed - index ${this.screenIndex} (${this.id})`,
    );

    this.cancelTimeoutFlyoutStale();
    this.updateAllLayoutPositions();
  }

  public async handleFlyoutInteracted(post: Post): Promise<void> {
    console.log(
      `[wall-engine-layout] Flyout interacted with post ${post.id} - index ${this.screenIndex} (${this.id})`,
    );

    this.startTimeoutFlyoutStale(post);
  }

  private async updateAllLayoutPositions(): Promise<void> {
    this.cancelTimeoutLayoutRotation();

    if (this.layout.layout) {
      const model = await getLayout(this.client, this.layout.layout);
      if (model) {
        const percentModel = model.content?.tileContentUpdatePct ?? 1;
        const percent = Math.min(Math.max(percentModel, 0), 1);

        this.emit("screen-updated", {
          id: this.id,
          active: true,
          index: this.screenIndex,
          layout: model,
          layoutPostPositions: this.managers
            .map((manager) => {
              if (Math.random() < percent) {
                return manager.getNextTileAsset();
              }
              return manager.getActiveAsset();
            })
            .filter((entry) => !!entry),
          menu: false,
          position: this.layout.position ?? undefined,
        });

        console.log(
          `[wall-engine-layout] Updated layout positions - index ${this.screenIndex} (${this.id})`,
        );

        this.cancelTimeoutLayoutRotation();

        const timeout =
          typeof model.content?.tileContentUpdateFrequency === "number" &&
          model.content.tileContentUpdateFrequency > 0 &&
          model.content.tileContentUpdateFrequency;

        if (timeout) {
          const timeoutMs = Math.max(timeout, 1) * 1000;
          if (timeoutMs > 0 && Number.isFinite(timeoutMs)) {
            this.timeoutTileAssetChange = setTimeout(() => {
              this.timeoutTileAssetChange = undefined;
              this.updateAllLayoutPositions();
            }, timeoutMs);
          }
        }
      }
    }
  }

  private cancelTimeoutLayoutRotation() {
    if (this.timeoutTileAssetChange) {
      clearTimeout(this.timeoutTileAssetChange);
      this.timeoutTileAssetChange = undefined;
      console.log(
        `[wall-engine-layout] Cancelled timeout for layout rotation - index ${this.screenIndex} (${this.id})`,
      );
    }
  }

  private cancelTimeoutFlyoutStale() {
    if (this.timeoutTileFlyoutStale) {
      clearTimeout(this.timeoutTileFlyoutStale);
      this.timeoutTileFlyoutStale = undefined;
      console.log(
        `[wall-engine-layout] Cancelled timeout for flyout stale - index ${this.screenIndex} (${this.id})`,
      );
    }
  }

  private async startTimeoutFlyoutStale(post?: Post) {
    this.cancelTimeoutFlyoutStale();

    if (this.layout.layout) {
      const model = await getLayout(this.client, this.layout.layout);
      if (model) {
        this.cancelTimeoutFlyoutStale();

        const timeout =
          post?.staleFlyoutTimeout ||
          model.content?.staleFlyoutTimeout ||
          DEFAULT_STALE_FLYOUT_TIMEOUT;

        if (typeof timeout === "number" && timeout > 0) {
          const timeoutMs = Math.max(timeout, 1) * 1000;
          if (timeoutMs > 0 && Number.isFinite(timeoutMs)) {
            this.timeoutTileFlyoutStale = setTimeout(() => {
              this.timeoutTileFlyoutStale = undefined;
              this.updateAllLayoutPositions();
            }, timeoutMs);
          }
        }
      }
    }
  }
}

type ScreenEngineEvents = LayoutEngineEvents & {
  "screens-updated": (screens: SliceWallScreenState[]) => void;
};

export class WallEngineScreen extends EventEmitter<ScreenEngineEvents> {
  private activating: boolean = false;
  private layouts: LayoutEngine[] = [];
  private states: SliceWallScreenState[] = [];

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly location?: string,
    private readonly profile?: string,
  ) {
    super();

    if (!this.location && !this.profile) {
      throw new Error("A location or profile is required");
    }
  }

  public destroy(): void {
    super.destroy();

    this.layouts.forEach((layout) => layout.destroy());
    this.layouts = [];

    console.log("[wall-engine-screen] Destroyed");
  }

  public async activate(): Promise<void> {
    if (this.activating) {
      return;
    }
    this.activating = true;
    console.log("[wall-engine-screen] Activating...");

    this.layouts.forEach((layout) => layout.destroy());
    this.layouts = [];

    const profile = this.profile
      ? await getProfile(this.client, this.profile)
      : await getLocationActiveProfile(this.client, this.location!);

    if (profile?.layouts) {
      const manager = new ProfileLayoutModelManager();

      for (let i = 0; i < profile.layouts.length; i++) {
        const layout = profile.layouts[i];
        const engine = new LayoutEngine(this.client, manager, layout, i);

        engine.on("screen-updated", (update) => {
          this.states[i] = update;
          this.emit("screen-updated", update);
        });
        engine.on("request-resting", (resting) => {
          this.emit("request-resting", resting);
        });
        engine.on("profile-changed", (profile) => {
          this.emit("profile-changed", profile);
        });

        this.layouts.push(engine);
      }
    }

    await Promise.all(this.layouts.map((layout) => layout.activate()));

    this.emit("screens-updated", this.states.slice());

    this.activating = false;
    console.log("[wall-engine-screen] Activated");
  }

  public disable(): void {
    this.layouts.forEach((layout) => layout.destroy());
    this.layouts = [];
    console.log("[wall-engine-screen] Disabled");
  }

  public async handlePostChange(screenId: string): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handlePostChange();
    }
  }

  public async handlePostInteracted(
    screenId: string,
    position: LayoutPostPosition,
  ): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handlePostInteracted(position);
    }
  }

  public async handlePostFlyoutDirectActivation(
    screenId: string,
    postId: string,
  ) {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handlePostFlyoutDirectActivation(postId);
    }
  }

  public handleFlyoutClosed(screenId: string): void {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      layout.handleFlyoutClosed();
    }
  }

  public async handleFlyoutInteracted(
    screenId: string,
    post: Post,
  ): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handleFlyoutInteracted(post);
    }
  }

  public async handleFlyoutKeepAlive(
    screenId: string,
    post: Post,
  ): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handleFlyoutInteracted(post);
    }
  }

  public async handleScreenMenuOpened(screenId: string): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handleScreenMenuOpened();
    }
  }

  public async handleScreenMenuClosed(screenId: string): Promise<void> {
    const layout = this.layouts.find((layout) => layout.id === screenId);
    if (layout) {
      await layout.handleScreenMenuClosed();
    }
  }
}
